import { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import Sidenav from "components/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import routes, { MyRoute } from "routes";

const MDBox = require("components/MDBox/index.js").default;

import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
  setAuth,
  setCurrentUser,
  setAuthToken,
  setTranslations,
  setCurrentRole,
  setBrands,
  setSettingPages,
} from "context";
import { checkAuthDO } from "utils/authUtils";

import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import { withErrorBoundary, useErrorBoundary } from "react-use-error-boundary";
import { ErrorPage } from "layouts/error/errorPage";
import { hasFeature, loadSettings } from "utils/settingsUtils";
import { getActiveSettingsPages, getSettingsPages } from "utils/boSettingsPageUtils";
import { BoSettingsPagesView } from "layouts/boSettingsPages/viewBoSettingsPage";
import { BoSettingsPage } from "models/BO/BoSettingsPage";
import { SystemSettings } from "layouts/appSettings/system";
import { getBrandsDO } from "utils/brandsUtils";
import { getTranslitions } from "utils/globalsUtils";
import { initDataDogRUM, initDataDogLogs, setUser, logInfo, logError } from "utils/datadog";

export default withErrorBoundary(({ children }) => {
  const [error, resetError] = useErrorBoundary(
    // You can optionally log the error to an error reporting service
    (error:any, errorInfo) => {
      // Ensure we're passing a proper error object with stack trace
      let errorObj = error;
      
      // If error is not an Error instance, create one
      if (!(error instanceof Error)) {
        errorObj = new Error(error?.message || String(error));
        // Preserve the original error info
        errorObj.stack = error?.stack || errorInfo?.componentStack || '';
      }
      
      logError('React Error Boundary caught error', errorObj, {
        componentStack: errorInfo?.componentStack,
        errorInfo: errorInfo,
      });
    } 
  );

  const navigate = useNavigate();

  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    auth,
    currentUser,
    currentRole,
    currentBrand,
    translations,
    settingPages
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null as any);
  const { pathname } = useLocation();
  const [filteredRoutes, setFilteredRoutes] = useState([] as MyRoute[]);
  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement!.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    setTimeout(() => {
      checkAuth();
      loadSettings();
    }, 500);

    getBrandsDO().then((res) => {
      setBrands(dispatch, res);
    });

    // Initialize DataDog monitoring
    initDataDogRUM();
    initDataDogLogs();
  }, []);

  useEffect(() => {
    /** get translations */
    getTranslitions(currentBrand).then((preparedTanslations) => {
      translations[currentBrand?.name] = preparedTanslations;
      setTranslations(dispatch, translations);
    });
  },[currentBrand]);

  // Track user in DataDog when currentUser changes
  useEffect(() => {
    if (currentUser && currentUser.id) {
      setUser({
        id: currentUser.id.toString(),
        name: currentUser.fullName,
        email: currentUser.email,
        brand: currentBrand?.name,
        role: currentRole ? `Role with ${currentRole.pages?.length || 0} pages` : 'No role',
      });
      logInfo('User logged in', { userId: currentUser.id, brand: currentBrand?.name });
    }
  }, [currentUser, currentBrand, currentRole]);

  const checkAuth = async () => {
    // const currentUser = JSON.parse(localStorage.getItem("BOCurrentUser"));
    // setCurrentUser(dispatch, currentUser);
    // setCurrentRole(
    //   dispatch,
    //   JSON.parse(localStorage.getItem("BORoles" + currentUser?.brands?.[0]))
    // );
    // setAuthToken(dispatch, localStorage.getItem("BOToken"));

    await checkAuthDO()
      .then(() => {})
      .catch((rej) => {
        if (rej?.response?.status === 401) {
          setAuth(dispatch, false);
          localStorage.removeItem("BOToken");
          localStorage.removeItem("BOCurrentUser");
          navigate("/sign-in");
        }
      });
  };

  useEffect(() => {
    setFilteredRoutes(generateFilteredRoutes(clonedRoutes()));
    getActiveSettingsPages().then((settingsPages) => 
      setSettingPages(dispatch, settingsPages));
  },[currentRole,currentBrand]);


  const clonedRoutes = () => {
    return routes.map((route) => {
      const clone = { ...route };
      if (route.collapse) {
        clone.collapse = route.collapse.map((r) => {
          return { ...r };
        });
      }
      return clone;
    })

  }

  const generateFilteredRoutes = (routes:MyRoute[]) => {
    return routes
      .filter((route:MyRoute) => route.accessNumber == 0 || !currentRole.pages || currentRole.pages.includes(route.accessNumber!))
      .filter((route:MyRoute) => !route.feature || hasFeature(route.feature!))
      .map((route) => {
        if (route.collapse) {
          route.collapse = generateFilteredRoutes(route.collapse);
        }
        return route;
      })
    }

  const getRoutes = (allRoutes:MyRoute[]) => {
    const route = allRoutes.find((route) => route.key === "settings");
    if (route && route.collapse && settingPages.length > 0) {
      route.collapse = settingPages.map((page) => ({
        name: page.title,
        key: page.id,
        route: `/brandSettings/${page.id}`,
        accessNumber: 0,
        component: <SystemSettings pageId={page.id} />,
      }));
    }
    const routes = allRoutes
      .map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      if (route.route) {
        return <Route path={route.route} element={route.component} key={route.key} />;
      }
      return null;
    });
    return routes;
  }

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />

        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor as any}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="mujual"
              routes={filteredRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor as any}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="mujual"
            routes={filteredRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          {/* <Configurator /> */}
          {/* {configsButton} */}
        </>
      )}
      {layout === "vr" && <Configurator />}
      {error ? (
        <ErrorPage error={error} resetError={resetError} />
      ) : (
        <Routes >
          {getRoutes(routes)}
          {auth ? (
            <Route path="*" element={<Navigate to="/dashboard" />} />
          ) : (
            <Route path="*" element={<Navigate to="/sign-in" />} />
          )}
        </Routes>
      )}
    </ThemeProvider>
  );
});
